"use client";

import { Capacitor } from "@capacitor/core";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { useBackAwarePush } from "@/hooks/use-back-aware-push";
import { hasBackNavigationInterceptor } from "@/hooks/use-back-navigation";
import { isSafeInternalHref } from "@/lib/back-nav";
import { goBackTo } from "@/lib/go-back";
import { internalAppHref } from "@/lib/link-url";
import { swipeBackCommittedRecently } from "@/lib/swipe-back/swipe-back-registry";
import { consumeNativeModalBack } from "@/lib/ui/native-modal-back";

/**
 * Android hardware/gesture back shares the visible back button's intent. iOS
 * keeps only the native link handling here; its interactive edge swipe (and
 * Android's in-app one) lives in `SwipeBackStage`.
 */
export function NativeBackButton() {
  const router = useRouter();
  const push = useBackAwarePush();
  const pushRef = useRef(push);
  pushRef.current = push;
  useEffect(() => {
    let disposed = false;
    const cleanups: Array<() => void> = [];
    void (async () => {
      const platform = Capacitor.getPlatform();
      if (disposed || (platform !== "android" && platform !== "ios")) return;
      const App =
        platform === "android" ? (await import("@capacitor/app")).App : null;
      if (disposed) return;
      const onLinkClick = (event: MouseEvent) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          !(event.target instanceof Element)
        )
          return;
        const anchor = event.target.closest("a[href]");
        if (!anchor || anchor.hasAttribute("download")) return;
        const path = internalAppHref(anchor.getAttribute("href") ?? "");
        if (!path) return;
        event.preventDefault();
        pushRef.current(path);
      };
      document.addEventListener("click", onLinkClick);
      cleanups.push(() => document.removeEventListener("click", onLinkClick));
      if (platform !== "android" || !App) return;
      const back = (canGoBack: boolean) => {
        // The system back gesture shares the left edge with our in-app swipe.
        // If that swipe just navigated, this is the same gesture arriving a
        // second time — going back again would skip a page.
        if (swipeBackCommittedRecently()) return;
        if (consumeNativeModalBack()) return;
        // Drawers/unsaved forms have first refusal, as with the system button.
        if (hasBackNavigationInterceptor()) {
          window.history.back();
          return;
        }
        const request = new Event("hiive:native-back", { cancelable: true });
        if (!window.dispatchEvent(request)) return;
        const from = new URLSearchParams(window.location.search).get("from");
        if (isSafeInternalHref(from)) {
          goBackTo(router, from);
        } else if (canGoBack) {
          router.back();
        } else {
          void App.minimizeApp();
        }
      };
      const handle = await App.addListener("backButton", ({ canGoBack }) =>
        back(canGoBack),
      );
      if (disposed) {
        void handle.remove();
        return;
      }
      cleanups.push(() => void handle.remove());
    })();
    return () => {
      disposed = true;
      for (const cleanup of cleanups) cleanup();
    };
  }, [router]);
  return null;
}
